// Realtime subscriptions over `/ws`, bound to one device session. A stream
// authenticates with the session's access token, re-sends `hello` before
// the token expires, resubscribes (resuming from the last applied cursor)
// after a reconnect and backs off between attempts. The caller applies the
// frames. `CircleStream` carries one circle; `ChatStream` (chat/) the inbox.
import type { WsClientMessage, WsServerMessage } from "@10twenty/api";
import type { StoredSession } from "../auth/session";
import type { CircleFrame } from "./liveCircle";

/** The parts of a browser `WebSocket` the stream uses. */
export interface SocketLike {
  send(data: string): void;
  close(code?: number, reason?: string): void;
  onopen: ((event: Event) => void) | null;
  onmessage: ((event: MessageEvent) => void) | null;
  onclose: ((event: CloseEvent) => void) | null;
  onerror: ((event: Event) => void) | null;
}

export type StreamStatus = "connecting" | "live" | "reconnecting" | "stopped";

export interface RealtimeStreamDeps {
  url: string;
  /** The device session this stream belongs to; it stops when that session ends. */
  deviceId: string;
  connect: (url: string) => SocketLike;
  /** The stored session with a usable access token (`SessionManager.activeSession`). */
  session: () => Promise<StoredSession | null>;
  /** Rotates a rejected token (`SessionManager.refresh`). */
  refresh: (stale: StoredSession) => Promise<StoredSession | null>;
  onStatus: (status: StreamStatus) => void;
  now?: () => number;
}

export interface CircleStreamDeps extends RealtimeStreamDeps {
  circleId: string;
  /** Cursor of the last applied frame, to resume from. */
  cursor: () => string | null;
  onFrame: (frame: CircleFrame) => void;
}

const MIN_BACKOFF_MS = 1_000;
const MAX_BACKOFF_MS = 60_000;
/**
 * `hello` is repeated this long before the token expires. It is inside the
 * session's 30 s refresh margin, so `activeSession` hands out a new token.
 */
const REHELLO_BEFORE_EXPIRY_MS = 25_000;
const MIN_REHELLO_DELAY_MS = 5_000;
/** Silence tolerated beyond two heartbeat intervals before the socket is presumed dead. */
const WATCHDOG_GRACE_MS = 10_000;
/** How long an open socket may wait for `welcome`. */
const WELCOME_TIMEOUT_MS = 20_000;

/** One authenticated socket with one subscription; subclasses route its frames. */
export abstract class RealtimeStream {
  private readonly deps: Required<RealtimeStreamDeps>;
  private socket: SocketLike | null = null;
  private stopped = false;
  private attempts = 0;
  private everLive = false;
  /** The session whose token the current socket last presented. */
  private helloSession: StoredSession | null = null;
  /** A token the server rejected; it is refreshed before the next connection. */
  private rejected: StoredSession | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | undefined;
  private rehelloTimer: ReturnType<typeof setTimeout> | undefined;
  private watchdogTimer: ReturnType<typeof setTimeout> | undefined;
  private watchdogMs = 0;

  constructor(deps: RealtimeStreamDeps) {
    this.deps = { now: Date.now, ...deps };
  }

  /** The subscription sent once per socket, after its first `welcome`. */
  protected abstract subscription(): WsClientMessage;

  /** A frame other than `welcome`, `heartbeat`, `pong` and the `unauthorized` error. */
  protected abstract handle(frame: WsServerMessage): void;

  /** The subscription was confirmed. */
  protected markLive(): void {
    this.attempts = 0;
    this.everLive = true;
    this.deps.onStatus("live");
  }

  start(): void {
    void this.connect();
  }

  stop(): void {
    if (this.stopped) return;
    this.stopped = true;
    this.clearTimers();
    this.dropSocket(1000);
    this.deps.onStatus("stopped");
  }

  /** Reconnects now if waiting to retry (for example when the browser comes back online). */
  nudge(): void {
    if (this.stopped || this.socket || this.reconnectTimer === undefined) return;
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = undefined;
    this.attempts = 0;
    void this.connect();
  }

  private async connect(): Promise<void> {
    this.deps.onStatus(this.everLive ? "reconnecting" : "connecting");
    let session: StoredSession | null;
    try {
      const rejected = this.rejected;
      this.rejected = null;
      session = rejected ? await this.deps.refresh(rejected) : await this.deps.session();
    } catch {
      // Network failure while refreshing; the session is kept.
      if (!this.stopped) this.scheduleReconnect();
      return;
    }
    if (this.stopped) return;
    if (session?.deviceId !== this.deps.deviceId) {
      this.stop(); // Signed out, or another sign-in replaced this session.
      return;
    }

    const socket = this.deps.connect(this.deps.url);
    this.socket = socket;
    let subscribed = false;
    socket.onopen = () => {
      if (this.socket !== socket) return;
      this.helloSession = session;
      this.watchdogMs = WELCOME_TIMEOUT_MS;
      this.armWatchdog();
      this.send({ type: "hello", protocol: 1, token: session.accessToken });
    };
    socket.onmessage = (event) => {
      if (this.socket !== socket || typeof event.data !== "string") return;
      this.armWatchdog();
      let frame: WsServerMessage;
      try {
        frame = JSON.parse(event.data) as WsServerMessage;
      } catch {
        return;
      }
      switch (frame.type) {
        case "welcome":
          this.watchdogMs = frame.heartbeat_interval_s * 2_000 + WATCHDOG_GRACE_MS;
          this.armWatchdog();
          this.scheduleRehello();
          if (!subscribed) {
            subscribed = true;
            this.send(this.subscription());
          }
          return;
        case "heartbeat":
        case "pong":
          return;
        case "error":
          if (frame.code === "unauthorized") {
            this.reconnectWithRefresh(socket);
            return;
          }
      }
      this.handle(frame);
    };
    socket.onclose = (event) => {
      if (this.socket !== socket) return;
      // 4001: the token expired or was revoked.
      if (event.code === 4001) this.rejected = this.helloSession;
      this.socket = null;
      this.lostConnection();
    };
    socket.onerror = () => {
      // Always followed by `close`.
    };
  }

  protected send(message: WsClientMessage): void {
    this.socket?.send(JSON.stringify(message));
  }

  /** The server rejected the presented token: refresh it and connect again. */
  private reconnectWithRefresh(socket: SocketLike): void {
    if (this.socket !== socket) return;
    this.rejected = this.helloSession;
    this.dropSocket(1000);
    this.lostConnection();
  }

  private lostConnection(): void {
    this.clearTimers();
    if (this.stopped) return;
    this.deps.onStatus(this.everLive ? "reconnecting" : "connecting");
    this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    const delay = Math.min(MAX_BACKOFF_MS, MIN_BACKOFF_MS * 2 ** this.attempts);
    this.attempts++;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = undefined;
      void this.connect();
    }, delay);
  }

  private scheduleRehello(): void {
    clearTimeout(this.rehelloTimer);
    const session = this.helloSession;
    if (!session) return;
    const delay = Math.max(MIN_REHELLO_DELAY_MS, session.accessExpiresAt - this.deps.now() - REHELLO_BEFORE_EXPIRY_MS);
    this.rehelloTimer = setTimeout(() => void this.rehello(), delay);
  }

  private async rehello(): Promise<void> {
    const socket = this.socket;
    let session: StoredSession | null;
    try {
      session = await this.deps.session();
    } catch {
      // Try again shortly; if the token expires first, the server closes with 4001.
      if (this.socket === socket && socket)
        this.rehelloTimer = setTimeout(() => void this.rehello(), MIN_REHELLO_DELAY_MS);
      return;
    }
    if (this.stopped || this.socket !== socket || !socket) return;
    if (session?.deviceId !== this.deps.deviceId) {
      this.stop();
      return;
    }
    this.helloSession = session;
    // The welcome that answers it schedules the next one.
    this.send({ type: "hello", protocol: 1, token: session.accessToken });
  }

  private armWatchdog(): void {
    clearTimeout(this.watchdogTimer);
    if (this.watchdogMs <= 0 || !this.socket) return;
    const socket = this.socket;
    this.watchdogTimer = setTimeout(() => {
      if (this.socket !== socket) return;
      this.dropSocket(4000);
      this.lostConnection();
    }, this.watchdogMs);
  }

  /** Detaches and closes the current socket; its late events are ignored. */
  private dropSocket(code: number): void {
    const socket = this.socket;
    this.socket = null;
    this.watchdogMs = 0;
    if (!socket) return;
    socket.onopen = socket.onmessage = socket.onclose = socket.onerror = null;
    try {
      socket.close(code);
    } catch {
      // Already closing.
    }
  }

  private clearTimers(): void {
    clearTimeout(this.reconnectTimer);
    clearTimeout(this.rehelloTimer);
    clearTimeout(this.watchdogTimer);
    this.reconnectTimer = this.rehelloTimer = this.watchdogTimer = undefined;
  }
}

/** One circle's frames. */
export class CircleStream extends RealtimeStream {
  private readonly circle: CircleStreamDeps;

  constructor(deps: CircleStreamDeps) {
    super(deps);
    this.circle = deps;
  }

  protected subscription(): WsClientMessage {
    const cursor = this.circle.cursor();
    return { type: "subscribe", circle_id: this.circle.circleId, ...(cursor ? { cursor } : {}) };
  }

  protected handle(frame: WsServerMessage): void {
    const circleId = this.circle.circleId;
    switch (frame.type) {
      // This socket never sends chat_subscribe, so chat frames are not for it.
      case "welcome":
      case "heartbeat":
      case "pong":
      case "chat_subscribed":
      case "chat_resync":
      case "chat_message":
      case "chat_conversation":
      case "chat_read":
      case "chat_typing":
      case "chat_receipt":
        return;
      case "error":
        // The hub reports a failed subscribe or delivery (`forbidden`, `not_found`)
        // without `circle_id`; this socket only carries one circle.
        if (frame.circle_id === undefined || frame.circle_id === circleId) {
          this.circle.onFrame({ ...frame, circle_id: circleId });
        }
        return;
      case "subscribed":
        if (frame.circle_id !== circleId) return;
        this.markLive();
        this.circle.onFrame(frame);
        return;
      default:
        if (frame.circle_id === circleId) this.circle.onFrame(frame);
    }
  }
}

/** The `/ws` URL on the app's own origin. */
export function streamUrl(origin: string): string {
  const url = new URL("/ws", origin);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return url.href;
}
